export { decodeJwt } from "./jwt.js";
export { Auth0Provider } from "./providers/auth0.js";
export { GoogleGisProvider } from "./providers/google-gis.js";
export { MockAuthProvider } from "./providers/mock.js";
export { DemoAuthProvider } from "./providers/demo.js";
import { Auth0Provider } from "./providers/auth0.js";
import { GoogleGisProvider } from "./providers/google-gis.js";
import { MockAuthProvider } from "./providers/mock.js";
import { DemoAuthProvider } from "./providers/demo.js";
import { reactive, watch } from "vue";
export function createAuthProvider(opts) {
    const providers = normalizeProviders(opts.config);
    const instances = providers.map((provider) => createSingleAuthProvider(opts, provider));
    if (instances.length === 1) {
        return instances[0];
    }
    return new MultiAuthProvider(`${opts.appName}-active-auth-provider`, instances);
}
function normalizeProviders(config) {
    const providers = config.providers.length ? config.providers : ["mock"];
    return Array.from(new Set(providers));
}
function createSingleAuthProvider(opts, provider) {
    const { appName, config } = opts;
    if (provider === "auth0") {
        if (!config.auth0) {
            throw new Error("auth.auth0 config is required when auth provider 'auth0' is enabled");
        }
        return new Auth0Provider({
            ...config.auth0,
            tokenStorageKey: `${appName}-auth0-token-set`,
            pkceStorageKey: `${appName}-auth0-pkce`,
        });
    }
    if (provider === "google") {
        if (!config.google) {
            throw new Error("auth.google config is required when auth provider 'google' is enabled");
        }
        return new GoogleGisProvider({
            ...config.google,
            storageKey: `${appName}-google-gis-credential`,
        });
    }
    if (provider === "demo") {
        if (!config.demo) {
            throw new Error("auth.demo config is required when auth provider 'demo' is enabled");
        }
        return new DemoAuthProvider({
            ...config.demo,
            apiUrl: opts.apiUrl,
            storageKey: `${appName}-demo-token`,
        });
    }
    if (provider === "backend") {
        throw new Error("Backend auth provider is not implemented yet");
    }
    return new MockAuthProvider({
        storageKey: `${appName}-mock-auth-user`,
        user: opts.mockUser,
    });
}
export class MultiAuthProvider {
    activeStorageKey;
    authProviders;
    name = "multi";
    providers;
    state;
    activeProvider = null;
    constructor(activeStorageKey, authProviders) {
        this.activeStorageKey = activeStorageKey;
        this.authProviders = authProviders;
        this.providers = authProviders.map((provider) => provider.name);
        this.state = reactive({
            status: "idle",
            user: null,
            idToken: null,
            accessToken: null,
            error: null,
        });
        this.authProviders.forEach((provider) => {
            watch(() => provider.state.status, () => {
                if (provider === this.activeProvider) {
                    this.copyState(provider);
                }
            });
        });
    }
    async init() {
        this.state.status = "initializing";
        let authenticatedProvider = null;
        for (const provider of this.orderedProvidersForInit()) {
            await provider.init();
            if (provider.state.status === "authenticated") {
                authenticatedProvider = provider;
                break;
            }
            if (provider.state.status === "error") {
                this.copyState(provider);
                return;
            }
        }
        if (authenticatedProvider) {
            this.activeProvider = authenticatedProvider;
            sessionStorage.setItem(this.activeStorageKey, authenticatedProvider.name);
            this.copyState(authenticatedProvider);
            return;
        }
        this.activeProvider = null;
        this.state.user = null;
        this.state.idToken = null;
        this.state.accessToken = null;
        this.state.error = null;
        this.state.status = "unauthenticated";
    }
    async login(providerName, redirectTo, credentials) {
        const provider = this.findProvider(providerName ?? this.providers[0]);
        this.activeProvider = provider;
        sessionStorage.setItem(this.activeStorageKey, provider.name);
        await provider.login(undefined, redirectTo, credentials);
        this.copyState(provider);
    }
    async logout() {
        await Promise.all(this.authProviders.map((provider) => provider.logout()));
        sessionStorage.removeItem(this.activeStorageKey);
        this.activeProvider = null;
        this.state.user = null;
        this.state.idToken = null;
        this.state.accessToken = null;
        this.state.error = null;
        this.state.status = "unauthenticated";
    }
    async handleCallback(url) {
        const activeName = sessionStorage.getItem(this.activeStorageKey);
        const provider = this.activeProvider ?? (activeName ? this.findProvider(activeName) : null);
        const callbackProvider = provider?.handleCallback ? provider : this.authProviders.find((candidate) => candidate.handleCallback);
        const redirect = await callbackProvider?.handleCallback?.(url);
        if (callbackProvider) {
            this.activeProvider = callbackProvider;
            sessionStorage.setItem(this.activeStorageKey, callbackProvider.name);
            this.copyState(callbackProvider);
        }
        return redirect;
    }
    orderedProvidersForInit() {
        const activeName = sessionStorage.getItem(this.activeStorageKey);
        if (!activeName)
            return this.authProviders;
        const active = this.findProvider(activeName);
        return [active, ...this.authProviders.filter((provider) => provider !== active)];
    }
    findProvider(name) {
        const provider = this.authProviders.find((candidate) => candidate.name === name);
        if (!provider) {
            throw new Error(`Unknown auth provider: ${String(name)}`);
        }
        return provider;
    }
    copyState(provider) {
        this.state.status = provider.state.status;
        this.state.user = provider.state.user;
        this.state.idToken = provider.state.idToken;
        this.state.accessToken = provider.state.accessToken;
        this.state.error = provider.state.error;
    }
}
//# sourceMappingURL=index.js.map